import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UpsertAddressFormFields } from 'addresses/components';
import { useAddContactGroupByEmailContext } from 'contacts/components/AddContactGroupByEmail/state';
import { ContactGroupType } from 'contacts/enums';
import { Form, FormField } from 'core/components';
import { FormSectionHeader } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { corporateService } from 'corporate/services';
import { Level } from 'theme/enums';

import { DefaultPricingFormField } from './DefaultPricingFormField';
import { SwitchField } from './SwitchField';

interface IProps {
  defaultDomain?: string;
  level?: number;
}

export const AddContactGroupFormFields = forwardRef(function AddContactGroupFormFields(
  { defaultDomain, level }: IProps,
  ref
) {
  const { t } = useTranslation();

  const { contactGroupType } = useAddContactGroupByEmailContext();

  const { trigger, getValues, watch, setValue } = useFormContext();

  const domainsValue = watch('domains');

  useEffect(() => {
    if (domainsValue.length === 0 && defaultDomain) {
      setValue('domains', [{ id: defaultDomain, title: defaultDomain, disabled: true }]);
    }
  }, [domainsValue, defaultDomain, setValue]);

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
    }),
    [getValues, trigger]
  );

  const switchFields = useMemo(() => {
    return [
      {
        name: 'isCustomer',
        label: t('labelContactGroupIsCustomer'),
        disabled: contactGroupType === ContactGroupType.CUSTOMER,
      },
      {
        name: 'isVendor',
        label: t('labelContactGroupIsVendor'),
        disabled: contactGroupType === ContactGroupType.VENDOR,
      },
      {
        name: 'automatedPricing',
        label: t('labelContactGroupAutomatedPricing'),
      },
      {
        name: 'autoLeadTimeEnabled',
        label: t('labelContactGroupAutoLeadTimeEnabled'),
      },
      {
        name: 'showInventory',
        label: t('labelContactGroupShowInventory'),
      },
      {
        name: 'freightEnabled',
        label: t('labelContactGroupFreightEnabled'),
      },
    ];
  }, [contactGroupType, t]);

  return (
    <Form isOpen inline withoutButtons withoutNotification level={level}>
      <>
        <FormField
          name="name"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelContactGroupName'), required: true }}
        />

        <FormField
          name="website"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelWebsite') }}
        />

        <FormField
          name="contactGrouping"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelContactGrouping'),
            getOptions: corporateService.getContactGroupings,
            enabledOnFocus: false,
          }}
        />

        <DefaultPricingFormField />

        <FormField
          name="domains"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelDomains'),
            options: [],
            getLabel: (option: { title: string }) => option.title,
            hasCreateOption: true,
            multiple: true,
            enabledOnFocus: false,
          }}
        />

        {switchFields.map((field) => (
          <SwitchField key={field.name} {...field} />
        ))}

        <FormSectionHeader
          title={t('labelDefaultAddress')}
          level={Level.TITLE_MD}
          sx={{ paddingTop: '8px' }}
        />

        <UpsertAddressFormFields fieldsPrefix="defaultAddress" />
      </>
    </Form>
  );
});
